// ═══════════════════════════════════════════════════════════════
// V·IANet Flow — NodeTooltip: Premium hover card for flowchart nodes
// 4 sections: Descripcion, Responsable, Documentos, Herramientas
// ═══════════════════════════════════════════════════════════════

import { FLOW_TOKENS as T } from '../shared/flowTokens';
import type { FlowNodeData, ActivityNodeData } from '../types/flowchart.types';

interface NodeTooltipProps {
  data: FlowNodeData;
  position: { x: number; y: number };
  visible: boolean;
}

export default function NodeTooltip({ data, position, visible }: NodeTooltipProps) {
  if (!visible) return null;

  const d = data as ActivityNodeData;
  const hasDesc = !!d.description;
  const hasResp = !!d.puestoNombre;
  const hasDocs = d.documentos && d.documentos.length > 0;
  const hasTools = d.herramientas && d.herramientas.length > 0;
  const hasContent = hasDesc || hasResp || hasDocs || hasTools;

  if (!hasContent) return null;

  return (
    <div
      style={{
        position: 'absolute',
        left: position.x,
        top: position.y - 10,
        transform: 'translate(-50%, -100%)',
        zIndex: 50,
        pointerEvents: 'none',
      }}
    >
      <div
        style={{
          backgroundColor: '#FFFFFF',
          borderRadius: 10,
          border: '1px solid #E2E6ED',
          borderTop: `3px solid ${T.color.navy}`,
          padding: 0,
          width: 260,
          boxShadow: '0 8px 24px rgba(31,56,100,0.18), 0 2px 6px rgba(0,0,0,0.06)',
          fontFamily: T.font.family,
          overflow: 'hidden',
        }}
      >
        {/* Descripcion */}
        {hasDesc && (
          <Section label="Descripcion" icon="📋" first>
            <p style={{ margin: 0, fontSize: 11, color: '#374151', lineHeight: 1.45 }}>
              {d.description}
            </p>
          </Section>
        )}

        {/* Responsable */}
        {hasResp && (
          <Section label="Responsable" icon="👤" first={!hasDesc}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <div
                style={{
                  width: 24,
                  height: 24,
                  borderRadius: 6,
                  background: T.color.navy,
                  color: '#fff',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 10,
                  fontWeight: 700,
                  flexShrink: 0,
                }}
              >
                {(d.puestoNombre ?? '').charAt(0).toUpperCase()}
              </div>
              <span style={{ fontSize: 11, color: '#1A1D23', fontWeight: 500 }}>
                {d.puestoNombre}
              </span>
            </div>
          </Section>
        )}

        {/* Documentos Asociados */}
        {hasDocs && (
          <Section label="Documentos Asociados" icon="📄" first={!hasDesc && !hasResp}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              {(d.documentos ?? []).map((doc) => (
                <div key={doc.id} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span
                    style={{
                      fontSize: 9,
                      fontFamily: "'JetBrains Mono', monospace",
                      color: T.color.secondary,
                      fontWeight: 600,
                    }}
                  >
                    {doc.codigo}
                  </span>
                  <span
                    style={{
                      fontSize: 10,
                      color: '#374151',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {doc.titulo}
                  </span>
                </div>
              ))}
            </div>
          </Section>
        )}

        {/* Herramientas Asociadas */}
        {hasTools && (
          <Section
            label="Herramientas Asociadas"
            icon="🔧"
            first={!hasDesc && !hasResp && !hasDocs}
          >
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
              {(d.herramientas ?? []).map((h, i) => (
                <span
                  key={i}
                  style={{
                    fontSize: 10,
                    padding: '2px 8px',
                    borderRadius: 10,
                    background: '#F0F4F8',
                    color: '#1F3864',
                    fontWeight: 500,
                    border: '1px solid #E2E6ED',
                  }}
                >
                  {h}
                </span>
              ))}
            </div>
          </Section>
        )}
      </div>

      {/* Arrow */}
      <div
        style={{
          width: 0,
          height: 0,
          margin: '0 auto',
          borderLeft: '6px solid transparent',
          borderRight: '6px solid transparent',
          borderTop: '6px solid #FFFFFF',
          filter: 'drop-shadow(0 1px 1px rgba(0,0,0,0.08))',
        }}
      />
    </div>
  );
}

// ─── Section component ──────────────────────────────────────────────────────

function Section({
  label,
  icon,
  first,
  children,
}: {
  label: string;
  icon: string;
  first?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div
      style={{
        padding: '10px 14px',
        borderTop: first ? 'none' : '1px solid #F0F2F5',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 5,
          marginBottom: 6,
        }}
      >
        <span style={{ fontSize: 11 }}>{icon}</span>
        <span
          style={{
            fontSize: 9,
            fontWeight: 700,
            color: T.color.navy,
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
          }}
        >
          {label}
        </span>
      </div>
      {children}
    </div>
  );
}
